import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { AsYouType, parsePhoneNumberWithError } from 'libphonenumber-js';
import { FORM_MAX_LENGTH } from '../../constants/form-limits';
import { phoneE164Validator } from '../../validators/phone.validator';

/**
 * Reusable Text Input Component
 * Based on Bootstrap 5 form controls with ADN theming
 * Implements ControlValueAccessor for reactive forms integration
 */
@Component({
  selector: 'app-form-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormInputComponent),
      multi: true
    }
  ]
})
export class FormInputComponent implements ControlValueAccessor, Validator {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() type: 'text' | 'email' | 'tel' | 'url' | 'date' | 'number' = 'text';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorMessage: string = '';
  @Input() helperText: string = '';

  /**
   * Optional input masking. Opt-in — defaults to `none` so existing
   * usages are unaffected.
   *   - `digits` : strip non-digits (capped by `maxLength` if set).
   *   - `zip`    : digits only, hard-capped at 5.
   *   - `phone`  : US number, displayed as `(555) 123-4567` and capped at
   *                10 national digits. The form-control value is NOT the
   *                display string: it's E.164 (`+15551234567`) once
   *                libphonenumber can build one, and the raw digits before
   *                that. Validates itself — the control gets a `phone`
   *                error for an invalid number, so pages only add
   *                `Validators.required`.
   *   - `email`  : strip whitespace (you can't type spaces into an email).
   */
  @Input() mask: 'none' | 'digits' | 'zip' | 'phone' | 'email' = 'none';

  /** Max digit count for the `digits` mask. Ignored by the other masks. */
  @Input() maxLength: number | null = null;

  /** What the field displays. For most masks this is also the control value. */
  value: string = '';
  onChange: any = () => {};
  onTouched: any = () => {};

  /**
   * HTML `maxlength` attribute for the rendered input (caps paste too).
   * Falls back to the app-wide default so no field is ever uncapped.
   */
  get maxLengthAttr(): number | null {
    switch (this.mask) {
      case 'phone':
        return 14; // "(555) 123-4567"
      case 'zip':
        return 5;
      case 'digits':
        return this.maxLength;
      default:
        return this.maxLength ?? FORM_MAX_LENGTH.DEFAULT;
    }
  }

  /** `inputmode` hint so mobile keyboards show the right keys. */
  get inputMode(): string | null {
    switch (this.mask) {
      case 'phone':
        return 'tel';
      case 'zip':
      case 'digits':
        return 'numeric';
      case 'email':
        return 'email';
      default:
        return null;
    }
  }

  /**
   * Apply the active mask to a raw value. Returns the sanitized /
   * formatted string the field should display and store.
   */
  private applyMask(raw: string): string {
    const value = raw ?? '';
    switch (this.mask) {
      case 'digits': {
        const digits = value.replace(/\D/g, '');
        return this.maxLength != null ? digits.slice(0, this.maxLength) : digits;
      }
      case 'zip':
        return value.replace(/\D/g, '').slice(0, 5);
      //Inputs go to (888) 888 - 8888 form with the AsYouType and allows backspace on area code
      case 'phone': {
        const raw = /^\(\d{3}$/.test(value) ? value.slice(0, -1) : value;
        const digits = raw.replace(/\D/g, '').slice(0, 10);
        return digits ? new AsYouType('US').input(digits) : '';
      }
      case 'email':
        return value.replace(/\s/g, '');
      default:
        return value;
    }
  }

  /**
   * The value handed to the form control for a given display string.
   * Only `phone` differs — every other mask stores what it shows.
   */
  private toControlValue(display: string): string {
    if (this.mask !== 'phone' || !display) return display;

    try {
      return parsePhoneNumberWithError(display, 'US').number;
    } catch {
      // A partial number has no E.164 form yet. Hand over the digits so
      // `validate()` reports it as invalid — '' would read as "required".
      return display.replace(/\D/g, '');
    }
  }

  /**
   * Validator hook — the form calls this with the control value after every
   * change. Only `phone` validates itself; other masks leave it to the page.
   */
  validate(control: AbstractControl): ValidationErrors | null {
    return this.mask === 'phone' ? phoneE164Validator(control) : null;
  }

  /**
   * Write value from form control to component. A `phone` control holds
   * E.164, whose leading country code would otherwise count toward the
   * 10-digit cap — so it's reduced to the national number first.
   */
  writeValue(value: string): void {
    let incoming = value || '';
    if (this.mask === 'phone' && incoming.startsWith('+')) {
      try {
        incoming = parsePhoneNumberWithError(incoming).nationalNumber;
      } catch {
        // Not parseable as E.164; the mask still reduces it to digits.
      }
    }
    this.value = this.applyMask(incoming);
  }

  /**
   * Register onChange callback
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Register onTouched callback
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Handle disabled state
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Handle input change
   */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.applyMask(input.value);
    // Reflect the sanitized value back into the DOM immediately so the
    // user never sees rejected characters (covers typing AND paste).
    if (input.value !== masked) {
      input.value = masked;
    }
    // Sets input (user sees)
    this.value = masked;
    // Sets actual value (component sees)
    this.onChange(this.toControlValue(masked));
  }

  /**
   * Handle input blur
   */
  onInputBlur(): void {
    this.onTouched();
  }
}
